<div class="price-details">
  @if (isLoading()) {
    <div class="price-details-loading">
      <mat-spinner diameter="50"></mat-spinner>
      <p>{{ 'COMMON.STATES.LOADING' | translate }}</p>
    </div>
  }

  @if (!isLoading() && !data()) {
    <div class="price-details-error">
      <p>{{ 'PRICE_DETAILS.ERROR' | translate }}</p>
    </div>
  }

  @if (!isLoading() && data()) {
    <md-page-header [title]="data()?.name?.[currentLocale] || ('PRICE_DETAILS.UNNAMED' | translate)" icon="price"></md-page-header>

    <!-- Basic Information -->
    <div class="price-details-section">
      <h1 class="price-details-section-title">{{ 'PRICE_DETAILS.BASIC_INFO' | translate }}</h1>
      <mat-divider></mat-divider>
      <div class="price-details-grid">
        <md-info-block [title]="'PRICE_DETAILS.NAME_FI' | translate" [text]="data()?.name?.fi"></md-info-block>
        <md-info-block [title]="'PRICE_DETAILS.NAME_EN' | translate" [text]="data()?.name?.en"></md-info-block>
        <md-info-block [title]="'PRICE_DETAILS.NAME_SV' | translate" [text]="data()?.name?.sv"></md-info-block>
      </div>

      @if (isEditing()) {
        <p class="price-details-form-hint">{{ 'PRICE_DETAILS.NAME_HINT' | translate }}</p>
      }
    </div>

    <!-- Product Information -->
    <div class="price-details-section">
      <h1 class="price-details-section-title">{{ 'PRICE_DETAILS.PRODUCT_INFO' | translate }}</h1>
      <mat-divider></mat-divider>
      <div class="price-details-grid">
        <md-info-block [title]="'PRICE_DETAILS.PRODUCT_TYPE' | translate" [text]="data()?.productType"></md-info-block>
        <md-info-block [title]="'FIELDS.PRODUCT' | translate" [text]="getProductName(data()?.productId || '')"></md-info-block>
      </div>

      @if (isEditing()) {
        <form [formGroup]="priceForm">
          <div class="price-details-form-grid">
            <mat-form-field>
              <mat-label>{{ 'PRICE_DETAILS.PRODUCT_TYPE' | translate }}</mat-label>
              <mat-select formControlName="productType">
                @for (type of productTypes; track type) {
                  <mat-option [value]="type">{{ type }}</mat-option>
                }
              </mat-select>
            </mat-form-field>

            <mat-form-field>
              <mat-label>{{ 'FIELDS.PRODUCT' | translate }}</mat-label>
              <mat-select formControlName="productId">
                @for (product of byProducts(); track product.id) {
                  <mat-option [value]="product.id">
                    {{ product.name[currentLocale] }}
                  </mat-option>
                }
              </mat-select>
            </mat-form-field>
          </div>
        </form>
      }
    </div>

    <!-- Partner Information -->
    <div class="price-details-section">
      <h1 class="price-details-section-title">{{ 'PRICE_DETAILS.PARTNER_INFO' | translate }}</h1>
      <mat-divider></mat-divider>
      <div class="price-details-grid">
        <md-info-block [title]="'PRICE_DETAILS.PARTNER_TYPE' | translate" [text]="data()?.partnerType"></md-info-block>
        <md-info-block [title]="'PRICE_DETAILS.PARTNER_ID' | translate" [text]="data()?.partnerId"></md-info-block>
      </div>
    </div>

    <!-- Current Price -->
    <div class="price-details-section">
      <h1 class="price-details-section-title">{{ 'PRICE_DETAILS.CURRENT_PRICE' | translate }}</h1>
      <mat-divider></mat-divider>
      @if (data()?.currentRate) {
        <div class="price-details-grid">
          <md-info-block
            [title]="'PRICE_DETAILS.VALUE' | translate"
            [text]="data()?.currentRate?.rate?.toString() + ' ' + getUnitDisplayName(data()?.unit || '')"
          ></md-info-block>
          <md-info-block [title]="'PRICE_DETAILS.PRICE_UNIT' | translate" [text]="getUnitDisplayName(data()?.unit || '')"></md-info-block>
          <md-info-block [title]="'PRICE_DETAILS.VALID_FROM' | translate" [text]="data()?.currentRate?.validFrom | date: 'dd.MM.yyyy'"></md-info-block>
        </div>
      } @else {
        <p class="price-details-empty-message">{{ 'PRICE_DETAILS.NO_PRICE' | translate }}</p>
      }

      <!-- Add new rates when editing -->
      @if (isEditing()) {
        <div class="price-details-new-rates">
          <form [formGroup]="priceForm" class="price-details-unit-form">
            <mat-form-field>
              <mat-label>{{ 'PRICE_DETAILS.PRICE_UNIT' | translate }}</mat-label>
              <mat-select formControlName="unit">
                @for (unit of measures; track unit.id) {
                  <mat-option [value]="unit.id">{{ unit.name }}</mat-option>
                }
              </mat-select>
            </mat-form-field>
          </form>

          <h3>{{ 'PRICE_DETAILS.ADD_NEW_PRICE' | translate }}</h3>
          <form [formGroup]="newRateForm" (ngSubmit)="addNewRate()" class="price-details-rate-form">
            <mat-form-field>
              <mat-label>{{ 'PRICE_DETAILS.PRICE' | translate }}</mat-label>
              <input matInput type="number" formControlName="rate" placeholder="0.00" min="0" step="0.01" />
              <span matSuffix>{{ getUnitDisplayName(priceForm.get('unit')?.value) }}</span>
              @if (newRateForm.get('rate')?.hasError('required')) {
                <mat-error>{{ 'VALIDATION.PRICE_REQUIRED' | translate }}</mat-error>
              }
              @if (newRateForm.get('rate')?.hasError('min')) {
                <mat-error>{{ 'VALIDATION.PRICE_MINIMUM' | translate }}</mat-error>
              }
            </mat-form-field>

            <mat-form-field>
              <mat-label>{{ 'PRICE_DETAILS.VALID_FROM' | translate }}</mat-label>
              <input matInput [matDatepicker]="picker" formControlName="validFrom" />
              <mat-datepicker-toggle matSuffix [for]="picker"></mat-datepicker-toggle>
              <mat-datepicker #picker></mat-datepicker>
              @if (newRateForm.get('validFrom')?.hasError('required')) {
                <mat-error>{{ 'VALIDATION.DATE_REQUIRED' | translate }}</mat-error>
              }
            </mat-form-field>

            <button mat-raised-button type="submit" color="primary" [disabled]="!newRateForm.valid">{{ 'COMMON.ACTIONS.ADD' | translate }}</button>
          </form>

          <!-- Display pending rates that will be saved -->
          @if (pendingRates().length > 0) {
            <div class="price-details-pending">
              <h4>{{ 'PRICE_DETAILS.PRICES_TO_SAVE' | translate }}</h4>
              <div class="price-details-pending-list">
                @for (rate of pendingRates(); track $index; let i = $index) {
                  <div class="price-details-pending-item">
                    <span>{{ rate.rate }} {{ getUnitDisplayName(priceForm.get('unit')?.value) }}</span>
                    <span>{{ rate.validFrom | date: 'dd.MM.yyyy' }}</span>
                    <button mat-icon-button color="warn" (click)="removeRate(i)" type="button" [attr.aria-label]="'COMMON.ACTIONS.REMOVE' | translate">
                      <mat-icon>delete</mat-icon>
                    </button>
                  </div>
                }
              </div>
            </div>
          }
        </div>
      }
    </div>

    <!-- Price List -->
    @if (data()?.allRates?.length) {
      <div class="price-details-section">
        <h1 class="price-details-section-title">{{ 'PRICE_DETAILS.PRICE_LIST' | translate }}</h1>
        <mat-divider></mat-divider>
        <div class="price-details-rates-grid">
          <div class="price-details-rates-header">
            <div>{{ 'PRICE_DETAILS.VALUE' | translate }}</div>
            <div>{{ 'PRICE_DETAILS.VALID_FROM' | translate }}</div>
          </div>
          @for (rate of data()?.allRates; track $index) {
            <div
              class="price-details-rates-item"
              [class.price-details-current-rate-highlight]="rate.validFrom === data()?.currentRate?.validFrom && rate.rate === data()?.currentRate?.rate"
            >
              <div>{{ rate.rate }} {{ getUnitDisplayName(data()?.unit || '') }}</div>
              <div>{{ rate.validFrom | date: 'dd.MM.yyyy' }}</div>
            </div>
          }
        </div>
      </div>
    }

    <!-- System Information -->
    <div class="price-details-section">
      <h1 class="price-details-section-title">{{ 'PRICE_DETAILS.SYSTEM_INFO' | translate }}</h1>
      <mat-divider></mat-divider>
      <div class="price-details-grid">
        <md-info-block [title]="'FIELDS.SYSTEM.CREATED_BY' | translate" [text]="data()?.createdBy || ('PRICE_DETAILS.UNKNOWN' | translate)"></md-info-block>
        <md-info-block [title]="'FIELDS.SYSTEM.CREATED_AT' | translate" [text]="data()?.createdAt | date: 'dd.MM.yyyy HH:mm'"></md-info-block>
        <md-info-block [title]="'FIELDS.SYSTEM.UPDATED_BY' | translate" [text]="data()?.updatedBy || ('PRICE_DETAILS.UNKNOWN' | translate)"></md-info-block>
        <md-info-block [title]="'FIELDS.SYSTEM.UPDATED_AT' | translate" [text]="data()?.updatedAt | date: 'dd.MM.yyyy HH:mm'"></md-info-block>
        <md-info-block [title]="'PRICE_DETAILS.SOURCE' | translate" [text]="data()?.source || ('PRICE_DETAILS.NOT_AVAILABLE' | translate)"></md-info-block>
        <md-info-block [title]="'PRICE_DETAILS.SOURCE_ID' | translate" [text]="data()?.sourceId || ('PRICE_DETAILS.NOT_AVAILABLE' | translate)"></md-info-block>
      </div>
    </div>

    <!-- Action buttons -->
    <div class="price-details-actions">
      <button mat-raised-button color="primary" (click)="isEditing() ? saveChanges() : enableEditing()">
        {{ isEditing() ? ('COMMON.ACTIONS.SAVE' | translate) : ('COMMON.ACTIONS.EDIT' | translate) }}
      </button>
      @if (isEditing()) {
        <button mat-button (click)="cancelEditing()">{{ 'COMMON.ACTIONS.CANCEL' | translate }}</button>
      }
    </div>

    <!-- Change History -->
    <md-audit-trail [auditTrail]="auditTrail()"></md-audit-trail>
  }
</div>
